/**
 * `useVmConnectionState` — renderer hook for the latest
 * `vm:connection-state` push payload.
 *
 * The producer (`main/vm-connection-state.ts`) dispatches a typed
 * `VmConnectionStatePush` whenever the derived 8-row priority table
 * computes a new state. The renderer caches the latest payload and
 * exposes the bare `state` discriminator (the 5-row enum
 * `idle | connecting | connected | reconnecting | unreachable`).
 *
 * Why a hook (rather than re-using the existing `VmStatusPill`
 * subscription): the Bridge-3 `<FurnaceBuildIndicator>` consumes the
 * VM-connection state to decide between `queued` / `building` /
 * `reconnecting` phases, but it lives in a sibling renderer surface
 * (the workspace header) and we don't want it to depend on the
 * pill's internal state. A standalone hook decouples the
 * consumer from the visual layer.
 *
 * Pre-event safety: returns `null` until the first push lands. The
 * `computeFurnaceBuildState` rule set treats `null` as "VM state
 * unknown" and falls through to `queued`, which is the safest
 * default for a turn that just dispatched.
 *
 * SSR / test safety: when `window` or `window.crucible` is absent
 * (static-render tests), the hook returns `null` and registers no
 * subscription.
 *
 * British spelling throughout.
 */

import { useEffect, useState } from 'react';
import type {
  VmConnectionState,
  VmConnectionStatePush,
} from '@volley/crucible-ipc-types/vm-connection-state';

/**
 * Subscribe to `vm:connection-state` push notifications and return
 * the latest state. The hook hides the full `VmConnectionStatePush`
 * payload from consumers that only care about the discriminator.
 *
 * @returns the latest `VmConnectionState` discriminator, or `null`
 * if no push has been received yet (or if the IPC bridge is
 * unavailable, e.g. under SSR).
 */
export function useVmConnectionState(): VmConnectionState | null {
  const [state, setState] = useState<VmConnectionState | null>(null);

  useEffect(() => {
    // SSR / test environments may not stub the bridge; bail rather
    // than throw.
    if (typeof window === 'undefined' || !window.crucible) return;

    const unsubscribe = window.crucible.on('vm:connection-state', (push: VmConnectionStatePush) => {
      setState(push.state);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  return state;
}
